import React, { useEffect, useRef, useState } from 'react';
import { GlobalWorkerOptions, getDocument } from 'pdfjs-dist';
// Vite: importer le worker en URL
import workerSrc from 'pdfjs-dist/build/pdf.worker.min.mjs?url';

GlobalWorkerOptions.workerSrc = workerSrc;

interface PdfCanvasViewerProps {
  url: string;
  className?: string;
  scale?: number;
}

export default function PdfCanvasViewer({ url, className, scale = 1.35 }: PdfCanvasViewerProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [rendering, setRendering] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    setRendering(true);

    const container = containerRef.current;
    if (!container) return;

    // Nettoyage DOM
    container.innerHTML = '';

    const loadingTask = getDocument({ url });

    (async () => {
      try {
        const pdf = await loadingTask.promise;
        if (cancelled) return;

        for (let pageNumber = 1; pageNumber <= pdf.numPages; pageNumber++) {
          if (cancelled) return;
          const page = await pdf.getPage(pageNumber);

          const viewport = page.getViewport({ scale });
          const dpr = window.devicePixelRatio || 1;

          const canvas = document.createElement('canvas');
          canvas.width = Math.floor(viewport.width * dpr);
          canvas.height = Math.floor(viewport.height * dpr);
          canvas.style.width = '100%';
          canvas.style.height = 'auto';

          const ctx = canvas.getContext('2d');
          if (!ctx) continue;

          ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

          const pageWrapper = document.createElement('div');
          pageWrapper.className = 'px-4 py-3';

          const pageCard = document.createElement('div');
          pageCard.className = 'rounded-md border bg-background overflow-hidden';
          pageCard.appendChild(canvas);

          pageWrapper.appendChild(pageCard);
          container.appendChild(pageWrapper);

          const renderTask = page.render({ canvasContext: ctx, viewport, canvas });
          await renderTask.promise;
        }
      } catch (e: any) {
        if (cancelled) return;
        setError(e?.message || 'Impossible de rendre le PDF');
      } finally {
        if (!cancelled) setRendering(false);
      }
    })();

    return () => {
      cancelled = true;
      // pdf.js cleanup
      try {
        loadingTask.destroy();
      } catch {
        // ignore
      }
    };
  }, [url, scale]);

  if (error) {
    return (
      <div className="h-[70vh] flex items-center justify-center text-sm text-muted-foreground">
        {error}
      </div>
    );
  }

  return (
    <div className={className}>
      {rendering && (
        <div className="h-[70vh] flex items-center justify-center text-sm text-muted-foreground">
          Rendu du PDF…
        </div>
      )}
      <div
        ref={containerRef}
        className={rendering ? 'hidden' : 'h-[70vh] overflow-auto'}
      />
    </div>
  );
}
